/**
 * Data layer for `kochava-generic-postback-template`. Thin typed fetch wrappers over /api/v1/kochava-generic-postback-template.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface KochavaGenericPostbackTemplateRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListKochavaGenericPostbackTemplateResponse {
	count: number;
	items: KochavaGenericPostbackTemplateRow[];
}

export interface CreateKochavaGenericPostbackTemplateBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/kochava-generic-postback-template";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListKochavaGenericPostbackTemplateResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListKochavaGenericPostbackTemplateResponse>(res);
}

export async function create(body: CreateKochavaGenericPostbackTemplateBody): Promise<KochavaGenericPostbackTemplateRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<KochavaGenericPostbackTemplateRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const kochavaGenericPostbackTemplateQueryKeys = {
	all: ["kochava-generic-postback-template"] as const,
	list: () => [...kochavaGenericPostbackTemplateQueryKeys.all] as const,
	detail: (id: string) => [...kochavaGenericPostbackTemplateQueryKeys.all, id] as const,
};
